/* eslint-disable no-case-declarations */
import { useEffect, useState } from 'react';
import useParams from '../../../globalHooks/useParams';
import useFilters from '../../../globalHooks/useFilters';
import { initialFinderFilterObj } from '../../../utils/constants';
import {
  fetchCollections,
  fetchDestinations,
  fetchHolidayFinder,
  fetchHolidayStylesFilters,
} from '../../../../modules/api';

const useHolidayFinder = () => {
  const paramsObJ = useParams();

  const [intro, setIntro] = useState(null);

  const [rawHolidays, setRawHolidays] = useState([]);

  const [holidays, setHolidays] = useState([]);

  const [holidaysLoaded, setHolidaysLoaded] = useState(false);

  const [featuredHolidays, setFeaturedHolidays] = useState([]);

  const [collections, setCollections] = useState([]);

  const [destinations, setDestinations] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);

  const [sort, setSort] = useState('popular');

  const [initialSetup, setInitialSetup] = useState(false);

  const [waiting, setWaiting] = useState(true);

  const [holidayStylesFilters, setHolidayStylesFilters] = useState([]);

  const [travelTypes, setTravelTypes] = useState([]);

  const [activeFilters, setActiveFilters] = useState({
    ...initialFinderFilterObj,
  });

  const {
    filterFinderDestination,
    filterFinderCollection,
    filterFinderStyle,
    filterTravelType,
  } = useFilters(activeFilters);

  useEffect(() => {
    (async () => {
      await setupHolidays();

      setHolidaysLoaded(true);

      setCollections(await fetchCollections());
      setDestinations(await fetchDestinations());
      setHolidayStylesFilters(await fetchHolidayStylesFilters());

      setInitialSetup(true);
      setWaiting(false);
    })();
  }, []);

  useEffect(() => {
    if (holidaysLoaded) {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      const type = params.get('type');
      const subtype = params.get('subtype');

      const travelType = type
        ? findTravelTypeBySlug(type, subtype)
        : findTravelTypeBySlug(initialFinderFilterObj.travelType.slug);

      setActiveFilters({
        ...activeFilters,
        travelType: travelType,
      });
    }
  }, [holidaysLoaded]);

  useEffect(() => {
    // time to setup filters from params if we have any
    if (initialSetup && paramsObJ) {
      let newFilterObj = { ...activeFilters };

      Object.keys(paramsObJ).forEach((param) => {
        switch (param) {
          case 'collection':
          case 'destination':
            const val = parseInt(paramsObJ[param], 10);

            if (!isNaN(val)) {
              newFilterObj = { ...newFilterObj, [param]: val };
            }
            break;
          case 'holiday-style':
            const holidayStyle = parseInt(paramsObJ[param], 10);

            if (!isNaN(holidayStyle)) {
              newFilterObj = {
                ...newFilterObj,
                holidayStyle: holidayStyle,
              };
            }
            break;
          case 'type':
            const travelType = paramsObJ.type;
            const subType = paramsObJ.subtype;

            newFilterObj = {
              ...newFilterObj,
              travelType: findTravelTypeBySlug(travelType, subType),
            };
            break;
          default:
            break;
        }
      });

      setActiveFilters(newFilterObj);
    }
  }, [initialSetup]);

  useEffect(() => {
    if (rawHolidays.length > 0) {
      // run filters on featured and non featured
      let newHolidays = filterFinderDestination(rawHolidays);
      let newFeaturedHolidays = filterFinderDestination(
        rawHolidays.filter(({ featured }) => featured)
      );

      newHolidays = filterFinderCollection(newHolidays);
      newHolidays = filterFinderStyle(newHolidays);

      // Check this doesn't apply to featured holidays
      newHolidays = filterTravelType(newHolidays);

      newFeaturedHolidays = filterFinderCollection(newFeaturedHolidays);
      newFeaturedHolidays = filterFinderStyle(newFeaturedHolidays);

      setFeaturedHolidays(newFeaturedHolidays);

      // run sort on non featured
      newHolidays = sortHolidays(sort, [...newHolidays]);
      setHolidays(newHolidays);

      setCurrentPage(0);

      // call function to update query string
      updateURL(activeFilters);
    }
  }, [JSON.stringify(activeFilters), JSON.stringify(rawHolidays), sort]);

  const findTravelTypeBySlug = (type, subType = null) => {
    const matchFunction = (travelType) => {
      // This could be more flexible if we simply check subType is not null or an empty string
      if (type === 'group' && subType) {
        return travelType.subtypeSlug === subType;
      }
      return travelType.slug === type;
    };

    const matchedTravelType = travelTypes.find(matchFunction);

    return matchedTravelType;
  };

  const updateURL = (activeFilters) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    params.set('type', activeFilters.travelType.slug);

    activeFilters.travelType.subtypeSlug
      ? params.set('subtype', activeFilters.travelType.subtypeSlug)
      : params.delete('subtype');

    url.search = params.toString();
    window.history.pushState({}, '', url);
  };

  const sortHolidays = (sort, holidays) => {
    switch (sort) {
      case 'popular':
        return holidays.sort(
          (a, b) => parseInt(a.popularity, 10) - parseInt(b.popularity, 10)
        );
      case 'new':
        return holidays;
      case 'price_low':
        // use low price
        return holidays.sort((a, b) => (a.price.slug > b.price.slug ? 1 : -1));
      case 'price_high':
        // use high price
        return holidays.sort((a, b) => (b.price.slug > a.price.slug ? 1 : -1));
      case 'a-z':
        // alphabet ascending
        return holidays.sort((a, b) =>
          a.title.display.charAt(0).toLowerCase() >
          b.title.display.charAt(0).toLowerCase()
            ? 1
            : -1
        );
      case 'z-a':
        // alphabet descending
        return holidays.sort((a, b) =>
          b.title.display.charAt(0).toLowerCase() >
          a.title.display.charAt(0).toLowerCase()
            ? 1
            : -1
        );
      default:
        return holidays;
    }
  };

  const setupHolidays = async () => {
    const { holidays: rawHolidays, intro } = await getHolidaysFeed();

    const featured = rawHolidays.filter(({ featured }) => featured);

    const notFeatured = rawHolidays.filter(({ featured }) => !featured);

    setRawHolidays(rawHolidays);

    setFeaturedHolidays(featured);

    setHolidays(notFeatured);

    setTravelTypes(getTravelTypes(rawHolidays));

    setIntro(intro);
  };

  const getTravelTypes = (feed) => {
    let returner = [
      {
        display: 'All',
        slug: 'All',
        subtypeSlug: '',
      },
    ];

    feed.forEach(({ travel_type }) => {
      if (travel_type) {
        const combinedSlug = travel_type.subtypeSlug
          ? `${travel_type.slug}-${travel_type.subtypeSlug}`
          : travel_type.slug;

        const alreadyExists = returner.some(
          ({ slug, subtypeSlug }) =>
            slug === travel_type.slug &&
            (subtypeSlug === travel_type.subtypeSlug ||
              (subtypeSlug === '' && !travel_type.subtypeSlug))
        );

        if (!alreadyExists && combinedSlug !== '') {
          returner = [...returner, travel_type];
        }
      }
    });

    return returner;
  };

  const getHolidaysFeed = async () => {
    try {
      return await fetchHolidayFinder();
    } catch (error) {
      console.error('Can not get holidays feed', error);
      throw error;
    }
  };

  // find and add or remove filter from activefilters
  const updateActiveFilters = (name, value) => {
    const found = activeFilters[name].find((item) => item.id === value.id);

    if (found) {
      const newItems = activeFilters[name].filter(
        (item) => item.id !== value.id
      );

      setActiveFilters({ ...activeFilters, [name]: newItems });
    } else {
      setActiveFilters({
        ...activeFilters,
        [name]: [...activeFilters[name], value],
      });
    }
  };

  const isActiveFilter = (name, value) => {
    return !!activeFilters[name].find((item) => item.id === value.id);
  };

  const flattenActiveFilters = (activeFilters) => {
    return [
      ...activeFilters.destination.map((element) => ({
        ...element,
        type: 'destination',
      })),
      ...activeFilters.collection.map((element) => ({
        ...element,
        type: 'collection',
      })),
      ...activeFilters.holidayStyle.map((element) => ({
        ...element,
        type: 'holidayStyle',
      })),
    ];
  };

  return {
    waiting,
    intro,
    holidays,
    featuredHolidays,
    collections,
    destinations,
    holidayStylesFilters,
    activeFilters,
    sort,
    setSort,
    currentPage,
    setCurrentPage,
    setActiveFilters,
    updateActiveFilters,
    travelTypes,
    isActiveFilter,
    flattenActiveFilters,
  };
};

export default useHolidayFinder;
